// Customer Mobile App Phase 2 (Sep 2026) — web data-deletion REQUEST page.
//
//   /retail/:slug/delete-my-data
//
// Google Play requires a web path for deletion requests (Apple requires
// the in-app path, which the app provides). The customer proves the
// phone/email with the existing OTP flow; the backend then RECORDS a
// request (`POST /customer/deletion-request`). Nothing is deleted here:
// retention policy is finalised by the business before any processing.
// Additive: no existing route or component is changed.
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;

function newSessionId() {
  try {
    if (window.crypto && window.crypto.randomUUID) return `del-${window.crypto.randomUUID()}`;
  } catch (_e) { /* fall through */ }
  return `del-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function errorMessage(e, fallback) {
  const d = e && e.response && e.response.data && e.response.data.detail;
  if (d && typeof d === "object" && d.message) return d.message;
  if (typeof d === "string") return fallback;
  return fallback;
}

export default function RetailDeleteMyData() {
  const { slug } = useParams();
  const sessionId = useMemo(newSessionId, []);
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [channel, setChannel] = useState("email");
  const [value, setValue] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [token, setToken] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${RETAIL_BASE}/${encodeURIComponent(slug)}/app-config`)
      .then((r) => setConfig(r.data))
      .catch(() => setLoadError(true));
  }, [slug]);

  const catalogId = config && config.storefront && config.storefront.catalog_ref;
  const businessName = (config && config.tenant && config.tenant.name) || "this business";

  async function sendCode(e) {
    e.preventDefault();
    setError("");
    if (channel === "sms" && !smsConsent) {
      setError("Please agree to receive a one-time verification text.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${RETAIL_BASE}/${encodeURIComponent(slug)}/otp/issue`, {
        session_id: sessionId, catalog_id: catalogId, destination_channel: channel,
        destination_value: value.trim(), purpose: "order_lookup",
        ...(channel === "sms" ? {
          sms_consent_given: true, sms_consent_disclosure_version: "v1.0",
          sms_consent_source_url: window.location.href,
        } : {}),
      });
      setToken(r.data.checkout_attempt_token);
    } catch (err) {
      setError(errorMessage(err, "We couldn't send a code. Please check the details and try again."));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    const proof = {
      catalog_id: catalogId, checkout_attempt_token: token,
      destination_channel: channel, destination_value: value.trim(),
    };
    try {
      await axios.post(`${RETAIL_BASE}/${encodeURIComponent(slug)}/otp/verify`, { ...proof, code: code.trim() });
      const r = await axios.post(`${RETAIL_BASE}/${encodeURIComponent(slug)}/customer/deletion-request`, proof);
      setResult(r.data.request);
    } catch (err) {
      setError(errorMessage(err, "That code didn't work. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 py-10" data-testid="retail-delete-my-data">
      <div className="max-w-md w-full bg-white rounded-xl shadow p-6 space-y-4">
        <h1 className="text-xl font-semibold text-slate-900">Request deletion of your data</h1>
        {loadError && <p className="text-sm text-red-700">This page isn't available right now. Please contact {businessName} directly.</p>}
        {config && !catalogId && <p className="text-sm text-slate-700">Please contact {businessName} directly to request deletion.</p>}
        {result ? (
          <div className="space-y-2" data-testid="retail-delete-my-data-done">
            <p className="text-sm text-slate-800">Your request has been recorded.</p>
            <p className="text-sm text-slate-600">Reference: <span className="font-mono">{result.request_id}</span></p>
            <p className="text-sm text-slate-600">
              {businessName} will review and complete it according to its data-retention policy.
              You have been signed out of the app on all devices.
            </p>
          </div>
        ) : catalogId && (
          <>
            <p className="text-sm text-slate-600">
              First, confirm it's you. We'll send a one-time code to the phone number or email you used with {businessName}.
            </p>
            {!token ? (
              <form onSubmit={sendCode} className="space-y-3">
                <div className="flex gap-2" role="radiogroup" aria-label="Verify by">
                  {["email", "sms"].map((c) => (
                    <button key={c} type="button" onClick={() => setChannel(c)} aria-pressed={channel === c}
                      className={`flex-1 rounded-lg border py-2 text-sm ${channel === c ? "border-slate-900 font-semibold" : "border-slate-300"}`}>
                      {c === "email" ? "Email" : "Phone"}
                    </button>
                  ))}
                </div>
                <input value={value} onChange={(e) => setValue(e.target.value)} required
                  type={channel === "email" ? "email" : "tel"} autoComplete={channel === "email" ? "email" : "tel"}
                  placeholder={channel === "email" ? "you@example.com" : "Mobile number"}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="retail-delete-my-data-value" />
                {channel === "sms" && (
                  <label className="flex gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} />
                    Text me a one-time verification code. Message and data rates may apply.
                  </label>
                )}
                <button disabled={busy} className="w-full rounded-lg bg-slate-900 text-white py-3 font-semibold disabled:opacity-50">
                  Send code
                </button>
              </form>
            ) : (
              <form onSubmit={confirm} className="space-y-3">
                <input value={code} onChange={(e) => setCode(e.target.value)} required inputMode="numeric"
                  autoComplete="one-time-code" placeholder="6-digit code"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2" data-testid="retail-delete-my-data-code" />
                <button disabled={busy} className="w-full rounded-lg bg-red-700 text-white py-3 font-semibold disabled:opacity-50">
                  Verify and request deletion
                </button>
              </form>
            )}
          </>
        )}
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      </div>
    </div>
  );
}
